/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
	Component,
	OnDestroy,
	ChangeDetectionStrategy,
	Inject,
	ChangeDetectorRef,
	OnInit,
	ViewChild,
	AfterContentInit,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import * as strings from '@touchplan/strings';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { delay, map, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { Project } from 'src/app/app-data/models/Project';
import {
	AbstractItemListEditorComponent,
	EditorMode,
	IListItemData,
	IListItemEventData,
	ItemType,
	IListItemFormEventData,
} from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { CustomFieldsApi, CustomFieldType } from 'src/app/app-data/state/CustomFieldsApi';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import { GenericDialogComponent } from 'src/app/common/generic-dialog/generic-dialog.component';
import { CustomFieldService } from '../services/custom-field.services';
import { ItemListComponent } from 'src/app/common/dialog-reusable/components/item-list.component';
import { RolesApi } from 'src/app/app-data/state/RolesApi';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { DefaultSplitDataRenderType, TicketType, Role } from '@touchplan/common';
import { AddEditCustomFieldComponent } from './add-edit-custom-field.component';
import { UserPreferenceStateService } from '../../../services/user-preference-state.service';
@Component({
	selector: 'app-custom-field',
	templateUrl: './custom-field-editor.component.html',
	styleUrls: ['./custom-field-editor.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomFieldEditorComponent
	extends AbstractItemListEditorComponent<CustomFieldDefinition>
	implements OnInit, OnDestroy, AfterContentInit
{
	@ViewChild('fieldList') private fieldList: ItemListComponent<CustomFieldDefinition>;
	@ViewChild('fieldEditor') private fieldEditor: AddEditCustomFieldComponent;

	// Project is not used yet during this framework setup. Need to pass in and use for functional implementation
	public project: Project;
	public rolesApi: RolesApi;
	public customFieldList$: Observable<CustomFieldDefinition[]>;
	// BehaviorSubject to force the list to be recreated when the user decides to show or hide the hidden custom fields.
	// See loadCustomFieldInitialData for more details.
	private resetCustomFieldList$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public resetCustomFieldListObs$ = this.resetCustomFieldList$.asObservable();
	public showHiddenCustomFieldControl = new FormControl(true);
	private customFieldsApi: CustomFieldsApi;
	private allRoles: Role[];

	// use enum in template
	private readonly ALL_ROLES = strings.CUSTOM_FIELD_ALL_ROLES;
	private readonly SCROLL_TIMEOUT = 200;

	public hasFilter = false;
	private itemForm: UntypedFormGroup;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data,
		protected dialogRef: MatDialogRef<CustomFieldEditorComponent>,
		protected dialog: MatDialog,
		private customFieldService: CustomFieldService,
		protected changeDetectorRef: ChangeDetectorRef,
		private userPreferenceStateService: UserPreferenceStateService
	) {
		super(changeDetectorRef, dialogRef);

		this.project = data.project;
		this.rolesApi = data.rolesApi;
		this.customFieldsApi = data.customFieldsApi;
		this.isAdmin = data.isAdmin;

		this.editorMode = EditorMode.Onside;
		this.self = this;
		this.itemType = {
			type: ItemType.CustomField,
			singularDisplay: strings.CUSTOM_FIELD_SINGULAR,
			pluralDisplay: strings.CUSTOM_FIELD_PLURAL,
			icon: 'sell',
		};

		// initialize editor action subject
		this.customFieldService.editorActionComplete(null);
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.dialogRef
			.beforeClosed()
			.pipe(take(1))
			.subscribe((value) => {
				this.doImplicitSave(true);
			});
		this.loadCustomFieldInitialData();
		this.saveHiddenCustomFieldPreferenceSubscription();
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	public ngAfterContentInit(): void {
		// apply the first filter
		this.applyFilter(this.ALL_ROLES);
		this.customFieldsApi.makeSureDefinitionOrdinalsAreValid();
	}

	// Override to change to a new workflow for creating new: add field first locally, update and then save.
	public startAdd(): void {
		// decide implicitly save or cancel possible editing of an existing or new field
		if (this.doImplicitSave()) {
			// check to see whether we reached the maximum allowed number of custom field definitions per project
			const max = this.customFieldService.getMaximumAllowedCustomFieldPerProject();
			if (this.customFieldsApi.getDefinitions().data.syncList.size + 1 > max) {
				this.showMaximumReachedDialog(max);

				return;
			}

			const data = {
				label: this.customFieldService.getDefaultDefinitionLabel(this.customFieldsApi),
				type: CustomFieldType.Text,
				isEnabled: true,
				ticketTypes: [TicketType.Task],
				roles: this.allRoles.map((role) => role.$id),
				order: this.customFieldsApi.getNewDefinitionOrder(),
			};

			// Add new is now the same as edit existing, the changeView to edit happens inside addDefinition
			const newKey = this.customFieldsApi.addDefinition(data);
			this.startEdit(this.customFieldsApi.getDefinitions().data.syncList.get(newKey), false);

			// item is add to the bottom. Scroll to it. give some time for the list updated on screen
			this.scrollToItem(this.itemInEdit);

			// goto the info tab
			this.fieldEditor?.gotoTab(0);
		}
	}

	public startEdit(item: CustomFieldDefinition, switchItemToEditView: boolean = true): void {
		// decide implicitly save or cancel possible editing of an existing or new field
		if (this.doImplicitSave(false, item)) {
			if (this.itemInEdit) {
				this.customFieldsApi.changeDefinitionView(this.itemInEdit.$id, DefaultSplitDataRenderType.database);
			}
			super.startEdit(item);
			if (item && switchItemToEditView) {
				this.customFieldsApi.changeDefinitionView(item.$id);
			}

			// goto the info tab
			this.fieldEditor?.gotoTab(0);
		}
	}

	// handle form related events for the field being edited
	public handleItemForm(emittedData: IListItemFormEventData<CustomFieldDefinition>) {
		this.itemForm = emittedData.form;
	}

	// this function is not in use due to add new is the same as edit existing.
	public completeAdd(emittedData: IListItemEventData<CustomFieldDefinition>): boolean {
		return true;
	}

	// For custom field, we use the local update first approach.Add new custom field becomes editing an yet to be
	// saved 'existing item'. As a result, both creating new and editing existing will come here
	public completeEdit(emittedData: IListItemEventData<CustomFieldDefinition>): boolean {
		let success = true;

		const item = emittedData.item;
		if (emittedData.type === 'Delete') {
			// deletion is not available in custom field at this time
		} else if (emittedData.type === 'Save') {
			const formData = emittedData.data;

			// maintain uniqueness of field label
			if (!this.customFieldsApi.isDefinitionLabelUnique(formData['itemInfoGroup']['label'], item)) {
				this.showUniquenessDialog();

				success = false;
			} else {
				// save changes using edit view so API can diff some values for analytics
				this.customFieldsApi.persistDefinition(item.$id, DefaultSplitDataRenderType.edit);
				// switch to database view
				this.customFieldsApi.changeDefinitionView(item.$id, DefaultSplitDataRenderType.database);
				// switch right back to edit view to allow continuous editing, to allow live editing/abort
				this.customFieldsApi.changeDefinitionView(item.$id);
			}
		} else if (emittedData.type === 'Cancel') {
			const isLocal = this.customFieldService.isAddingNewDefinition(item);
			this.customFieldsApi.abortDefinitionView(item.$id);
			this.customFieldsApi.changeDefinitionView(item.$id);

			// if cancelling creating new and not implicit cancel,re-select one if any
			if (isLocal && !(emittedData.event && emittedData.event['implicit'])) {
				this.itemInEdit = null;
				// need to select and edit the first matching one if one exists
				this.editFirstMatchingItem(true, item);
			}
		}

		this.customFieldService.editorActionComplete({ type: emittedData.type, item, status: success });
		return success;
	}

	public doReorder(event: CdkDragDrop<CustomFieldDefinition[]>, items: CustomFieldDefinition[]) {
		if (event.previousIndex !== event.currentIndex) {
			// Explanation of the issue and the solution for this piece of code:
			// When we drag and drop an item, the event.previousIndex and event.currentIndex are the indexes of whatever list is being displayed
			// in the UI (filtered or unfiltered). However, the moveDefinition method uses always the unfiltered list of items.
			// So we need to get the dragged item and the target item from the filtered list, and then find their original indexes in the unfiltered list.
			// For that we use the moveDefinitionByReference method, which takes the dragged item and the target item as parameters.
			// In that way, we can move the dragged item to the target index in the unfiltered list always.
			// Filtered list = list without custom fields that are not enabled (hidden).
			// Unfiltered list = list with all custom fields (enabled and disabled).
			// NOTE: Please note that this is not the same problem as the one explained in the loadCustomFieldInitialData method. That one was found after solving this one. :(
			// Original issue (this one): https://mocasystems.atlassian.net/browse/TP-7015

			// Get the dragged item and the target item from the filtered list
			const filteredList = items;
			const draggedItem = filteredList[event.previousIndex];
			const targetItem = filteredList[event.currentIndex];
			this.customFieldsApi.moveDefinitionByReference(draggedItem, targetItem);
		}
	}

	protected createItemListFilters(): Observable<string[]> {
		return this.rolesApi.data.list$.pipe(
			map((roleMap) => {
				this.allRoles = Array.from(roleMap.values());
				const filters = this.allRoles.map((role) => {
					return role.view.roleName;
				});
				filters.unshift(this.ALL_ROLES);
				return filters;
			})
		);
	}

	public doesItemMatchFilter(item: CustomFieldDefinition): boolean {
		let result = true;

		if (this.itemFilter && this.allRoles) {
			const which = this.allRoles.find((role) => role.view.roleName === this.itemFilter);
			result =
				this.itemFilter === this.ALL_ROLES ||
				(which && item.view.roles && item.view.roles.findIndex((id) => id === which.$id) >= 0);
		}

		return result;
	}

	public applyFilter(thisFilter: string): void {
		super.applyFilter(thisFilter);

		// need to select and edit the first matching one if one exists
		this.editFirstMatchingItem();
		this.hasFilter = thisFilter !== this.ALL_ROLES;
	}

	protected createItemListObservable(): Observable<CustomFieldDefinition[]> {
		return this.customFieldsApi.getOrderedDefinition$();
	}

	public getListItemData(field: CustomFieldDefinition): IListItemData {
		if (field) {
			return {
				leftLogo: {
					type: 'icon',
					value: field.view.isEnabled ? 'visibility' : 'visibility_off',
					color: field.view.isEnabled ? 'var(--touchplan-blue-dark)' : 'var(--grey)',
				},
				middleContent: [
					field.view.label,
					this.customFieldService.DataTypesMap.get(field.view.type as CustomFieldType),
				],
				rightSummary: {
					type: 'icon',
					value: 'drag_handle',
				},
			};
		} else {
			return null;
		}
	}

	public doesItemMatchSearch(field: CustomFieldDefinition, searchText: string): boolean {
		return (field && field.view.label ? field.view.label : '')
			.toLowerCase()
			.includes(searchText.toLowerCase());
	}

	private loadCustomFieldInitialData(): void {
		let showHiddenTickets = this.userPreferenceStateService.showCustomFieldsHiddenTickets;
		showHiddenTickets = showHiddenTickets === undefined || showHiddenTickets;

		const showHiddenCustomFieldControlChanged$ = this.showHiddenCustomFieldControl.valueChanges.pipe(
			startWith(showHiddenTickets)
		);

		this.customFieldList$ = combineLatest([
			this.getItemListObservable(),
			showHiddenCustomFieldControlChanged$,
		]).pipe(
			map(([items, showHiddenItems]) => items.filter((item) => showHiddenItems || item.view.isEnabled))
		);

		// Why do we need the resetCustomFieldList$ BehaviorSubject?
		// Because we need to "recreate" the list of custom fields when the user decides to show or hide the hidden custom fields.
		// Why do we need to "recreate" the list of custom fields?
		// Because for an unknown reason (material bug?), Material's cdkDropList caches the list of items and it may not align with either the full list or the visible list
		// This is combined solution between this and the ItemListComponent. The ItemListComponent has a *ngIf="itemList && !hideItemList" that will basically recreate the list.
		// NOTE: Please note, that even is this sounds like a similiar problem to the one explained in doReorder, it is not. This was found after solving that problem. :(
		// Original issue: https://mocasystems.atlassian.net/browse/TP-7015
		showHiddenCustomFieldControlChanged$
			.pipe(
				tap(() => this.resetCustomFieldList$.next(true)),
				delay(0)
			)
			.subscribe(() => {
				this.resetCustomFieldList$.next(false);
			});

		this.showHiddenCustomFieldControl.setValue(showHiddenTickets, { emitEvent: false });
	}

	private showUniquenessDialog(): void {
		this.dialog.open(GenericDialogComponent, {
			data: {
				title: this.strings.CUSTOM_FIELD_LABEL_UNIQUE_TEXT,
				yesButtonText: this.strings.UICONTROL_TEXT_OK,
			},
			disableClose: true,
		});
	}

	private showMaximumReachedDialog(max: number): void {
		this.dialog.open(GenericDialogComponent, {
			data: {
				title: this.strings.CUSTOM_FIELD_MAXIMUM_LIMIT_REACHED(max),
				yesButtonText: this.strings.UICONTROL_TEXT_OK,
			},
			disableClose: true,
		});
	}

	private editFirstMatchingItem(reverse: boolean = false, removed?: CustomFieldDefinition): void {
		this.customFieldList$.pipe(take(1)).subscribe((items) => {
			if (removed) {
				const where = items.findIndex((item) => item.$id === removed.$id);
				if (where >= 0) {
					items.splice(where, 1);
				}
			}
			if (reverse) {
				items = items.reverse();
			}
			let found = false;
			for (const item of items) {
				if (this.doesItemMatchFilter(item)) {
					found = true;
					this.startEdit(item);
					this.scrollToItem(item);
					break;
				}
			}
			if (!found) {
				this.startEdit(null);
			}
		});
	}

	private scrollToItem(item: CustomFieldDefinition): void {
		setTimeout(() => {
			// scroll to bottom, range indicate the top of the bottom element
			let where = this.fieldList.itemList.findIndex((field) => field.$id === item.$id);
			if (where < 0) {
				where = 0;
			}

			this.fieldList.virtualScroll.scrollToIndex(where * 10);

			// select label for editing if needed for the custom field that is being edited
			this.fieldEditor.selectToEditLabel();
		}, this.SCROLL_TIMEOUT);
	}

	// This is the implicit saving logic:
	// For existing field editing:
	//	1. Save when the form is dirty and valid
	//	2. Cancel if the form is dirty but not valid
	//	3. Do nothing
	// For creating a new field:
	// 	1. Save if the form is valid
	//	2. Cancel if the form is not valid
	// If user closes the dialog using the X on title bar, just Cancel
	private doImplicitSave(closingDialog = false, item?: CustomFieldDefinition): boolean {
		let result = true;
		// an item is being edited now
		if (this.itemInEdit && this.itemForm && (!item || item !== this.itemInEdit)) {
			// save if valid and dirty, cancel otherwise
			const saveOrCancel = closingDialog
				? 'Cancel'
				: this.customFieldService.isAddingNewDefinition(this.itemInEdit)
				? this.itemForm.valid
					? 'Save'
					: 'Cancel'
				: this.itemForm.dirty
				? this.itemForm.valid
					? 'Save'
					: 'Cancel'
				: null;

			if (saveOrCancel) {
				result = this.completeEdit({
					type: saveOrCancel,
					item: this.itemInEdit,
					event: { implicit: true },
					form: this.itemForm,
					data: this.itemForm.getRawValue(),
				});

				if (result) {
					this.itemForm = null;
				}
			}
		}

		return result;
	}

	private saveHiddenCustomFieldPreferenceSubscription(): void {
		this.userPreferenceStateService.showCustomFieldsHiddenTickets$ =
			this.showHiddenCustomFieldControl.valueChanges;
	}
}
